// The install guide: what each device must run, which file an account
// downloads for it, where the file goes and how the game starts. One source
// for the laptop's Install Guide app (desktop/apps/guide) and the page
// HELP_PATH (scripts/help.ts writes it at build time). Neither keeps words of
// its own.
//
// The laptop's theme faces are baked with ASCII 32..126 only, so every string
// here is ASCII; test/install-guide.test.ts holds the module to that, to one
// section per device, and to https links.
//
// The formats are docs/packages.md's and docs/publishing.md's. Games
// registered outside the Studio (Pocket Tokyo, Atlas and others) upload their
// own packages under their own folder names, so the text names "the folder
// inside the zip" and no fixed name. How a console is made to run homebrew is
// the community guides' subject: a section links to one and does not repeat
// its steps.

import type { Lang } from "./lang.ts";
import type { NativeTarget } from "./types.ts";
import { GUIDE_INTRO_JA, GUIDE_PARTS_JA, INSTALL_GUIDE_JA } from "./install-guide-ja.ts";

/** The documentation's path on the Studio's host (scripts/docs.ts writes it). */
export const DOCS_PATH = "/docs/";

/** The install guide's path: a page of the documentation. */
export const HELP_PATH = `${DOCS_PATH}install/`;

/** Where the guide was before it joined the documentation: a page there sends the visitor on (scripts/help.ts). */
export const LEGACY_HELP_PATH = "/help/install/";

/** The laptop app's package id: desktop/pocket.system.json installs it. */
export const GUIDE_PACKAGE = "dev.pocket-nexus.studio.guide";

/** A run of a paragraph: text, a link, or a name to type as it is (a path, a command, a file). */
export type Run = string | { link: string; href: string } | { code: string };
export type Paragraph = readonly Run[];

export interface GuideSection {
  device: NativeTarget;
  /** The section's anchor on the web page. */
  anchor: string;
  /** The device's name as the shelf calls it. */
  title: string;
  /** One line for the laptop's list: the file and what the device runs. */
  summary: string;
  /** What the device must run before a package installs, and the guide that gets it there. */
  needs: readonly Paragraph[];
  /** The file an account downloads. */
  file: readonly Paragraph[];
  /** From the file on a computer to the game running, in order. */
  steps: readonly Paragraph[];
  /** What else holds for this device. */
  notes: readonly Paragraph[];
}

/** The headings of a section's parts, on the page and in the app. */
export const GUIDE_PARTS = { needs: "What it needs", file: "The file", steps: "Installing" } as const;

/** What the page and the app say before the devices. */
export const GUIDE_INTRO: readonly Paragraph[] = [
  [
    "A package from Pocket Studio is homebrew: a program that is not from the maker of the device. ",
    "A console runs it once it has custom firmware or a homebrew loader, and an iPod touch once it is jailbroken. ",
    "An Android phone needs a setting.",
  ],
  [
    "Each section links to the community guide that sets a device up for homebrew, then says where the downloaded file goes and how the game starts. ",
    "A package is the file the device installs, or a ",
    { code: ".zip" },
    " that holds it with the files that go beside it. Unzip a ",
    { code: ".zip" },
    " on the computer first.",
  ],
];

export const INSTALL_GUIDE: readonly GuideSection[] = [
  {
    device: "psp",
    anchor: "psp",
    title: "PSP",
    summary: ".zip, custom firmware",
    needs: [
      [
        "A PSP that runs custom firmware. ARK-4 runs on every PSP model on firmware 6.60 or 6.61, and ",
        { link: "the ARK-4 wiki", href: "https://github.com/PSP-Archive/ARK-4/wiki" },
        " has its installation for each model.",
      ],
    ],
    file: [
      [
        "A ",
        { code: ".zip" },
        " with a folder ",
        { code: "PSP/GAME/<name>" },
        " in it. The folder holds ",
        { code: "EBOOT.PBP" },
        " and, for some games, data files beside it. The built-in games name the folder ",
        { code: "Studio<Name>" },
        "; other games choose their own name.",
      ],
      ["A game can also come as an ", { code: "EBOOT.PBP" }, " alone."],
    ],
    steps: [
      [
        "Connect the PSP to the computer with a USB cable and select USB Connection under Settings, or put its Memory Stick in a card reader.",
      ],
      [
        "Unzip the file. Copy the game's folder, the one inside ",
        { code: "PSP/GAME" },
        " in the zip, into ",
        { code: "PSP/GAME" },
        " on the Memory Stick. Make that folder if the Memory Stick has none.",
      ],
      [
        "For an ",
        { code: "EBOOT.PBP" },
        " alone, make a folder for the game in ",
        { code: "PSP/GAME" },
        " and put the file in it.",
      ],
      ["On the PSP, open Game, then Memory Stick, and start the game."],
    ],
    notes: [],
  },
  {
    device: "vita",
    anchor: "vita",
    title: "PS Vita",
    summary: ".vpk, HENkaku",
    needs: [
      [
        "A PS Vita that runs HENkaku, with VitaShell. ",
        { link: "vita.hacks.guide", href: "https://vita.hacks.guide/" },
        " installs HENkaku with molecularShell, which is VitaShell under another name, and then Enso.",
      ],
    ],
    file: [["A ", { code: ".vpk" }, ", the package format VitaShell installs."]],
    steps: [
      [
        "In VitaShell, press SELECT to start the connection to the computer: USB shows the memory card as a drive, FTP takes the file over Wi-Fi. START opens the settings where the SELECT button is set to USB or FTP.",
      ],
      ["Copy the ", { code: ".vpk" }, " to ", { code: "ux0:data/" }, " on the Vita."],
      ["In VitaShell, go to the file, press Cross on it and press Cross again to install it."],
      ["The game's bubble is on the LiveArea after that. Tap it to start the game."],
    ],
    notes: [
      [
        "The built-in games' packages are safe homebrew: they ask for no extended permissions, and HENkaku's setting for unsafe homebrew can stay off. ",
        "A game that is unsafe homebrew makes VitaShell ask about extended permissions during the install, and runs only with that setting on.",
      ],
    ],
  },
  {
    device: "3ds",
    anchor: "3ds",
    title: "Nintendo 3DS",
    summary: ".3dsx, Luma3DS",
    needs: [
      [
        "A Nintendo 3DS or 2DS with the custom firmware Luma3DS and the Homebrew Launcher. ",
        { link: "3ds.hacks.guide", href: "https://3ds.hacks.guide/" },
        " installs Luma3DS, and its last page, Finalizing Setup, puts the Homebrew Launcher on the HOME Menu.",
      ],
    ],
    file: [
      [
        "A ",
        { code: ".3dsx" },
        ", a program the Homebrew Launcher starts. It is not a CIA and installs nothing to the HOME Menu.",
      ],
      [
        "A game with data files on the SD card comes as a ",
        { code: ".zip" },
        " of the ",
        { code: ".3dsx" },
        " and those files.",
      ],
    ],
    steps: [
      ["Turn the 3DS off and put its SD card in the computer."],
      [
        "Copy the ",
        { code: ".3dsx" },
        " into the folder ",
        { code: "3ds" },
        " at the root of the SD card (",
        { code: "sdmc:/3ds/" },
        "), not into ",
        { code: "Nintendo 3DS" },
        ". For a ",
        { code: ".zip" },
        ", copy what is inside it to the root of the SD card with its folders as they are.",
      ],
      ["Put the card back, turn the 3DS on and open the Homebrew Launcher from the HOME Menu."],
      ["The Homebrew Launcher lists the programs in ", { code: "sdmc:/3ds/" }, ". Select the game to start it."],
    ],
    notes: [],
  },
  {
    device: "ipod-touch",
    anchor: "ipod-touch",
    title: "iPod touch",
    summary: ".ipa, iOS 6.1, jailbroken",
    needs: [
      [
        "An iPod touch (4th generation) on iOS 6.1.x, jailbroken, with Cydia. ",
        { link: "ios.cfw.guide", href: "https://ios.cfw.guide/get-started/iPod-touch-(4th-generation).html" },
        " gives the jailbreak for each iOS version of this iPod: Aquila, which is untethered.",
      ],
      [
        "Cydia on iOS 6 reaches most repositories today only with updated root certificates: open the Signed iOS Bundle from ",
        { link: "tlsroot.litten.ca", href: "https://tlsroot.litten.ca/" },
        " in Safari on the iPod and install it.",
      ],
      [
        "Then install AppSync Unified from Cydia. It supports iOS 5 and later and lets iOS install an app that Apple did not sign. ",
        { link: "Its page", href: "https://github.com/akemin-dayo/AppSync" },
        " names the repository to add. Restart the iPod once after it is installed.",
      ],
    ],
    file: [
      [
        "An ",
        { code: ".ipa" },
        ". The packages are made for the iPod touch 4th generation on iOS 6.1.x.",
      ],
    ],
    steps: [
      ["Connect the iPod to the computer with its USB cable."],
      [
        "Install the ",
        { code: ".ipa" },
        " with Legacy iOS Kit: start it, open App Management, choose Install IPA (appinst) and select the file. ",
        { link: "Its page on installing an IPA", href: "https://github.com/LukeZGD/Legacy-iOS-Kit/wiki/Install-IPA-AppSync" },
        " has what each install option needs.",
      ],
      ["The game's icon is on the Home screen after that. Tap it to start the game."],
    ],
    notes: [],
  },
  {
    device: "android",
    anchor: "android",
    title: "Android phone",
    summary: ".apk, Android 4.3 or later",
    needs: [
      [
        "A phone or tablet on Android 4.3 (API 18) or later, with a 32-bit ARM processor (armeabi-v7a) or a 64-bit one (arm64-v8a). ",
        "It needs no root.",
      ],
    ],
    file: [["An ", { code: ".apk" }, "."]],
    steps: [
      [
        "On Android 7.1 and earlier, open Settings, then Security, and turn on Unknown sources. ",
        "On Android 8 and later the phone asks when the file is opened: allow installs from the app that opens it.",
      ],
      [
        "Copy the ",
        { code: ".apk" },
        " to the phone with a USB cable, or download it on the phone, and open it from a file manager or the Downloads app.",
      ],
      ["Tap Install, then Open. The game is in the app list after that."],
    ],
    notes: [],
  },
];

/**
 * The whole guide in one language: what the page and the app show, in the
 * order they show it. Each language has the same sections, anchors and links;
 * test/install-guide.test.ts holds them to that. The laptop's app reads
 * `installGuide(lang)`; the English constants above are `installGuide("en")`.
 */
export interface InstallGuide {
  /** The page's and the app's name. */
  title: string;
  /** The line under the page's heading. */
  lead: string;
  /** The label of the list of devices. */
  devices: string;
  parts: { needs: string; file: string; steps: string };
  intro: readonly Paragraph[];
  sections: readonly GuideSection[];
}

const GUIDES: Record<Lang, InstallGuide> = {
  en: {
    title: "Install guide",
    lead: "How a package downloaded from Pocket Studio goes on each device.",
    devices: "Devices",
    parts: GUIDE_PARTS,
    intro: GUIDE_INTRO,
    sections: INSTALL_GUIDE,
  },
  ja: {
    title: "インストールガイド",
    lead: "Pocket Studio からダウンロードしたパッケージを、デバイスごとに入れる方法です。",
    devices: "デバイス",
    parts: GUIDE_PARTS_JA,
    intro: GUIDE_INTRO_JA,
    sections: INSTALL_GUIDE_JA,
  },
};

/** The guide in `lang`. */
export function installGuide(lang: Lang): InstallGuide {
  return GUIDES[lang];
}

/** A device's section, in `lang` (English by default). */
export function guideSection(device: NativeTarget, lang: Lang = "en"): GuideSection {
  const section = GUIDES[lang].sections.find((entry) => entry.device === device);
  if (!section) throw new Error(`the install guide has no section for ${device}`);
  return section;
}

/** The web page, or a device's section of it. */
export function guideUrl(device?: NativeTarget): string {
  return device ? `${HELP_PATH}#${guideSection(device).anchor}` : HELP_PATH;
}

/** An address as the laptop shows it: without its scheme and its last slash. */
export function shownAddress(href: string): string {
  return href.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

/**
 * A paragraph as plain text, for a screen with no links: a link reads as its
 * address when the address starts with the link's text, and otherwise as its
 * text followed by the address in parentheses.
 */
export function plainParagraph(paragraph: Paragraph): string {
  return paragraph
    .map((run) => {
      if (typeof run === "string") return run;
      if ("code" in run) return run.code;
      const address = shownAddress(run.href);
      return address.startsWith(run.link) ? address : `${run.link} (${address})`;
    })
    .join("");
}
